import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link2, BarChart3, Palette, LogOut, Sparkles, ExternalLink, Menu, X, Database, Building2, Package, Grid3x3, Inbox, Mail, Share2, FileText, Star, Video, Film, Image, Layers, CalendarDays, CalendarCheck, Eye, Users, Handshake, ScanLine, AtSign, PartyPopper, ClipboardCheck, Shrink, QrCode, Settings, Calendar, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import LinksEditor from '@/components/dashboard/LinksEditor';
import Analytics from '@/components/dashboard/Analytics';
import Appearance from '@/components/dashboard/Appearance';
import CRMIntegration from '@/components/dashboard/CRMIntegration';
import LeadsInbox from '@/components/dashboard/LeadsInbox';
import BusinessInfo from '@/components/dashboard/BusinessInfo';
import Products from '@/components/dashboard/Products';
import SocialGridEditor from '@/components/dashboard/SocialGridEditor';
import SocialLinksEditor from '@/components/dashboard/SocialLinksEditor';
import AboutEditor from '@/components/dashboard/AboutEditor';
import TestimonialsEditor from '@/components/dashboard/TestimonialsEditor';
import MediaEditor from '@/components/dashboard/MediaEditor';
import VideosEditor from '@/components/dashboard/VideosEditor';
import PostersEditor from '@/components/dashboard/PostersEditor';
import SectionOrderEditor from '@/components/dashboard/SectionOrderEditor';
import BookingEditor from '@/components/dashboard/BookingEditor';
import BookingsInbox from '@/components/dashboard/BookingsInbox';
import NetworkHub from '@/components/dashboard/NetworkHub';
import ReviewSetup from '@/components/dashboard/ReviewSetup';
import TradeSetup from '@/components/dashboard/TradeSetup';
import Scanner from '@/components/dashboard/Scanner';
import EmailSignature from '@/components/dashboard/EmailSignature';
import EventsManager from '@/components/dashboard/EventsManager';
import ReviewTracker from '@/components/dashboard/ReviewTracker';
import ShortLinks from '@/components/dashboard/ShortLinks';
import QRCodes from '@/components/dashboard/QRCodes';
import AIAssistant from '@/components/dashboard/AIAssistant';
import GoogleCalendar from '@/components/dashboard/GoogleCalendar';
import AdminPanel from '@/components/dashboard/AdminPanel';
import PlatformAdmin from '@/components/dashboard/PlatformAdmin';
import ChangePassword from '@/components/dashboard/ChangePassword';
import MyProfiles from '@/components/dashboard/MyProfiles';
import ProfileSwitcher from '@/components/dashboard/ProfileSwitcher';
import ProfilePreview from '@/components/dashboard/ProfilePreview';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, fetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themePreview, setThemePreview] = useState(null);

  const pathParts = location.pathname.split('/');
  const currentTab = pathParts[pathParts.length - 1] || 'links';

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (currentTab !== 'appearance') setThemePreview(null); }, [currentTab]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      setProfile(res.data);
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  }, []);

  useEffect(() => {
    if (!loading && currentTab === 'section-order') fetchProfile();
  }, [currentTab, fetchProfile, loading]);

  const fetchData = async () => {
    try {
      const [profileRes, linksRes, productsRes] = await Promise.all([
        axios.get(`${API}/profile`),
        axios.get(`${API}/links`),
        axios.get(`${API}/products`)
      ]);
      setProfile(profileRes.data);
      setLinks(linksRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSwitch = () => {
    fetchUser();
    fetchData();
  };
  const handleLogout = () => { logout(); navigate('/'); };

  const navItems = [
    { id: 'links', label: 'Links', icon: Link2, path: '/dashboard' },
    { id: 'social-icons', label: 'Social Icons', icon: Share2, path: '/dashboard/social-icons' },
    { id: 'section-order', label: 'Section Order', icon: Layers, path: '/dashboard/section-order' },
    { id: 'products', label: profile?.catalog_type === 'services' ? 'Services' : 'Products', icon: Package, path: '/dashboard/products' },
    { id: 'social-grid', label: 'Social Grid', icon: Grid3x3, path: '/dashboard/social-grid' },
    { id: 'about', label: 'Our Journey', icon: FileText, path: '/dashboard/about' },
    { id: 'testimonials', label: 'Testimonials', icon: Star, path: '/dashboard/testimonials' },
    { id: 'videos', label: 'Videos', icon: Film, path: '/dashboard/videos' },
    { id: 'posters', label: 'Posters', icon: Image, path: '/dashboard/posters' },
    { id: 'media', label: 'Profile Video', icon: Video, path: '/dashboard/media' },
    { id: 'business', label: 'Profile', icon: Building2, path: '/dashboard/business' },
    { id: 'appearance', label: 'Appearance', icon: Palette, path: '/dashboard/appearance' },
    { id: 'inbox', label: 'Leads', icon: Inbox, path: '/dashboard/inbox' },
    { id: 'booking', label: 'Booking Setup', icon: CalendarDays, path: '/dashboard/booking' },
    { id: 'bookings', label: 'Bookings', icon: CalendarCheck, path: '/dashboard/bookings' },
    { id: 'crm', label: 'CRM', icon: Database, path: '/dashboard/crm' },
    { id: 'network', label: 'Network Hub', icon: Users, path: '/dashboard/network' },
    { id: 'reviews', label: 'AI Reviews', icon: Star, path: '/dashboard/reviews' },
    { id: 'review-tracker', label: 'Review Tracker', icon: ClipboardCheck, path: '/dashboard/review-tracker' },
    { id: 'trade', label: 'Trade', icon: Handshake, path: '/dashboard/trade' },
    { id: 'events', label: 'Events', icon: PartyPopper, path: '/dashboard/events' },
    { id: 'scanner', label: 'Scanner', icon: ScanLine, path: '/dashboard/scanner' },
    { id: 'signature', label: 'Email Signature', icon: AtSign, path: '/dashboard/signature' },
    { id: 'short-links', label: 'Short Links', icon: Shrink, path: '/dashboard/short-links' },
    { id: 'qr-codes', label: 'QR Codes', icon: QrCode, path: '/dashboard/qr-codes' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/dashboard/analytics' },
    { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles, path: '/dashboard/ai-assistant' },
    { id: 'calendar', label: 'Google Calendar', icon: Calendar, path: '/dashboard/calendar' },
    { id: 'admin', label: 'Admin Panel', icon: Building2, path: '/dashboard/admin' },
    { id: 'platform-admin', label: 'Platform Admin', icon: Shield, path: '/dashboard/platform-admin', superAdminOnly: true },
  ];

  if (loading) {
    return (<div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-[#27272A] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
            <span className="font-outfit font-bold text-lg text-white">One Link</span>
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-white">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="lg:hidden fixed inset-0 z-40 bg-[#050505] pt-16 overflow-y-auto">
          <nav className="p-4 space-y-2 pb-20">
            {navItems.filter(item => !item.superAdminOnly || user?.is_super_admin).map((item) => (
              <button key={item.id} data-testid={`mobile-nav-${item.id}`} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${(currentTab === item.id || (currentTab === 'dashboard' && item.id === 'links')) ? 'bg-violet-500/20 text-violet-400' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
                <item.icon className="w-5 h-5" />{item.label}
              </button>
            ))}
            <div className="pt-4 border-t border-[#27272A] mt-4">
              <a data-testid="mobile-view-profile" href={`/${user?.username}`} target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 transition-all">
                <ExternalLink className="w-5 h-5" />createonelink.com/{user?.username}
              </a>
              <button data-testid="mobile-nav-my-profiles" onClick={() => { navigate('/dashboard/my-profiles'); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${currentTab === 'my-profiles' ? 'bg-violet-500/20 text-violet-400' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
                <Users className="w-5 h-5" />My Profiles
              </button>
              <button data-testid="mobile-nav-settings" onClick={() => { navigate('/dashboard/settings'); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${currentTab === 'settings' ? 'bg-violet-500/20 text-violet-400' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
                <Settings className="w-5 h-5" />Settings
              </button>
              <button data-testid="mobile-logout-btn" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all">
                <LogOut className="w-5 h-5" />Logout
              </button>
            </div>
          </nav>
        </motion.div>
      )}

      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 h-screen sticky top-0 bg-[#0a0a0a] border-r border-[#27272A] p-4 overflow-y-auto">
          <div className="flex items-center gap-2 px-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
            <span className="font-outfit font-bold text-lg text-white">One Link</span>
          </div>
          <div className="mb-4">
            <ProfileSwitcher onSwitch={handleProfileSwitch} />
          </div>
          <nav className="flex-1 space-y-1">
            {navItems.filter(item => !item.superAdminOnly || user?.is_super_admin).map((item) => (
              <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${(currentTab === item.id || (currentTab === 'dashboard' && item.id === 'links')) ? 'bg-violet-500/20 text-violet-400 border-l-2 border-violet-500' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
                <item.icon className="w-5 h-5" />{item.label}
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-4 border-t border-[#27272A]">
            <a data-testid="view-profile-link" href={`/${user?.username}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 transition-all">
              <ExternalLink className="w-4 h-4" /><span className="truncate">createonelink.com/{user?.username}</span>
            </a>
            <button data-testid="nav-my-profiles" onClick={() => navigate('/dashboard/my-profiles')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${currentTab === 'my-profiles' ? 'bg-violet-500/20 text-violet-400 border-l-2 border-violet-500' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
              <Users className="w-5 h-5" />My Profiles
            </button>
            <button data-testid="nav-settings" onClick={() => navigate('/dashboard/settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${currentTab === 'settings' ? 'bg-violet-500/20 text-violet-400 border-l-2 border-violet-500' : 'text-[#A1A1AA] hover:bg-white/5'}`}>
              <Settings className="w-5 h-5" />Settings
            </button>
            <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all">
              <LogOut className="w-5 h-5" />Logout
            </button>
          </div>
        </aside>

        <main className="flex-1 lg:flex pt-16 lg:pt-0">
          <div className="flex-1 p-6 lg:p-8 overflow-y-auto max-h-screen">
            <div className="flex items-center justify-end mb-4 lg:mb-0">
              <a data-testid="preview-profile-btn" href={`/${user?.username}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-400 hover:bg-violet-500/20 transition-all text-sm">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview Profile</span>
              </a>
            </div>
            <Routes key={profile?.user_id || 'loading'}>
              <Route index element={<LinksEditor links={links} setLinks={setLinks} profile={profile} setProfile={setProfile} />} />
              <Route path="social-icons" element={<SocialLinksEditor />} />
              <Route path="section-order" element={<SectionOrderEditor profile={profile} setProfile={setProfile} links={links} />} />
              <Route path="products" element={<Products profile={profile} setProfile={setProfile} products={products} setProducts={setProducts} />} />
              <Route path="social-grid" element={<SocialGridEditor />} />
              <Route path="about" element={<AboutEditor />} />
              <Route path="testimonials" element={<TestimonialsEditor />} />
              <Route path="media" element={<MediaEditor />} />
              <Route path="video" element={<VideosEditor setProfile={setProfile} />} />
              <Route path="videos" element={<VideosEditor setProfile={setProfile} />} />
              <Route path="poster" element={<PostersEditor setProfile={setProfile} />} />
              <Route path="posters" element={<PostersEditor setProfile={setProfile} />} />
              <Route path="business" element={<BusinessInfo profile={profile} setProfile={setProfile} />} />
              <Route path="appearance" element={<Appearance profile={profile} setProfile={setProfile} onPreviewTheme={setThemePreview} />} />
              <Route path="inbox" element={<LeadsInbox />} />
              <Route path="booking" element={<BookingEditor profile={profile} setProfile={setProfile} />} />
              <Route path="bookings" element={<BookingsInbox />} />
              <Route path="crm" element={<CRMIntegration profile={profile} setProfile={setProfile} />} />
              <Route path="network" element={<NetworkHub />} />
              <Route path="reviews" element={<ReviewSetup profile={profile} setProfile={setProfile} />} />
              <Route path="trade" element={<TradeSetup profile={profile} setProfile={setProfile} />} />
              <Route path="scanner" element={<Scanner />} />
              <Route path="events" element={<EventsManager />} />
              <Route path="review-tracker" element={<ReviewTracker profile={profile} setProfile={setProfile} />} />
              <Route path="signature" element={<EmailSignature />} />
              <Route path="short-links" element={<ShortLinks />} />
              <Route path="qr-codes" element={<QRCodes />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="ai-assistant" element={<AIAssistant />} />
              <Route path="calendar" element={<GoogleCalendar />} />
              <Route path="admin" element={<AdminPanel />} />
              <Route path="platform-admin" element={<PlatformAdmin />} />
              <Route path="settings" element={<ChangePassword />} />
              <Route path="my-profiles" element={<MyProfiles onSwitch={handleProfileSwitch} />} />
            </Routes>
          </div>
          <div className="hidden xl:block w-96 p-8 border-l border-[#27272A]">
            <div className="sticky top-8">
              <p className="text-sm text-[#A1A1AA] mb-4 text-center">Live Preview</p>
              <ProfilePreview profile={themePreview ? { ...profile, ...themePreview } : profile} links={links} products={products} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
